import { useCallback, useState } from "react";

/**
 * Tracks "this particular image URL failed to load" so a caller can render a
 * fallback instead of a broken-image icon.
 *
 * WHY THIS IS KEYED ON THE URL rather than being a plain boolean: a boolean
 * `imgFailed` flag latches. Every uploaded avatar and workspace logo in this
 * app was unloadable until the /static route existed (see
 * handler.PublicImage), so a long-lived component - the sidebar, the topbar,
 * the ImageUpload preview - would set that flag once and then keep showing
 * the fallback even after the URL changed to one that works. The same latch
 * hits the ordinary case too: replace a picture, the column gets a brand-new
 * UUID URL, and the preview stays stuck on the initials from the previous
 * failure.
 *
 * Storing WHICH url failed makes recovery automatic: a new url is simply not
 * the failed one, so it is attempted again with no reset call anywhere.
 */
export function useImageFallback(url?: string | null): {
  /** True when `url` is present and has not failed to load. */
  showImage: boolean;
  /** Pass to the <img>'s onError. */
  onError: () => void;
  /** Clears the failure for the current url - for the rare case where the
   * bytes behind an unchanged url are known to have changed. */
  reset: () => void;
} {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const onError = useCallback(() => setFailedUrl(url ?? null), [url]);
  const reset = useCallback(() => setFailedUrl(null), []);
  return {
    showImage: Boolean(url) && failedUrl !== url,
    onError,
    reset,
  };
}
